import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Clock, Crown, ExternalLink } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Slim sticky banner shown for active demo users.
 * - Countdown to demo_expires_at
 * - Link to /koupit (purchase license)
 * Hidden when user is not demo or has no expiry set.
 */
export default function DemoBanner() {
  const { user } = useAuth();
  const [remaining, setRemaining] = useState(null);

  useEffect(() => {
    const exp = user?.demo_expires_at;
    if (!user?.is_demo_user || !exp) {
      setRemaining(null);
      return;
    }
    const expMs = new Date(exp).getTime();
    const tick = () => {
      const diff = expMs - Date.now();
      if (diff <= 0) { setRemaining({ mm: 0, ss: 0, expired: true }); return; }
      setRemaining({
        mm: Math.floor(diff / 60000),
        ss: Math.floor((diff % 60000) / 1000),
        expired: false,
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [user]);

  if (!user?.is_demo_user || !remaining) return null;

  const lowTime = !remaining.expired && remaining.mm < 10;
  const cls = remaining.expired
    ? 'bg-red-600 border-red-700'
    : lowTime
      ? 'bg-amber-500 border-amber-600 text-slate-900'
      : 'bg-emerald-600 border-emerald-700';

  return (
    <div
      className={`w-full ${cls} text-white border-b shadow-sm sticky top-0 z-50`}
      data-testid="demo-banner"
    >
      <div className="max-w-7xl mx-auto px-3 sm:px-4 py-1.5 flex items-center justify-between gap-2 text-xs sm:text-sm">
        <div className="flex items-center gap-2 min-w-0">
          <Crown className="w-4 h-4 flex-shrink-0" />
          <span className="font-semibold whitespace-nowrap">Demo licence</span>
          <span className="hidden sm:inline opacity-90">·</span>
          <span className="flex items-center gap-1 whitespace-nowrap" data-testid="demo-banner-countdown">
            <Clock className="w-3.5 h-3.5" />
            {remaining.expired
              ? 'Demo vypršelo'
              : <>Zbývá <strong>{String(remaining.mm).padStart(2, '0')}:{String(remaining.ss).padStart(2, '0')}</strong></>}
          </span>
        </div>
        <Link
          to="/koupit"
          className="flex items-center gap-1.5 bg-white/15 hover:bg-white/25 active:bg-white/30 transition-colors px-3 py-1 rounded-md font-semibold whitespace-nowrap"
          data-testid="demo-banner-buy-btn"
        >
          Koupit licenci <ExternalLink className="w-3.5 h-3.5" />
        </Link>
      </div>
    </div>
  );
}
